import { Helmet } from "react-helmet-async";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check } from "react-feather";
import Screen from "../Layouts/Screen/Screen";
import { useAuth, useMessage } from "../../global-components/src";
import { errorMessage } from "../../global-components/src/utils/text";
import Spinner from "../../global-components/src/components/spinner/spiner";
import { api } from "../../services/api";

interface AvailableTag {
    key: string;
    group: string;
}

const ContentPreferences = () => {
    const { t } = useTranslation();
    const { token } = useAuth();
    const { setMessage } = useMessage();
    const [availableTags, setAvailableTags] = useState<AvailableTag[]>([]);
    const [blockedTags, setBlockedTags] = useState<string[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const load = async () => {
            try {
                const { data } = await api(token).get("/user/tag-preferences");
                setAvailableTags(data.available_tags ?? []);
                setBlockedTags(data.blocked_tags ?? []);
            } catch (err) {
                setMessage(errorMessage(err), "error");
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [token]);

    const toggleTag = async (key: string) => {
        if (saving) return;
        const previous = blockedTags;
        const next = previous.includes(key) ? previous.filter((tag) => tag !== key) : [...previous, key];
        setBlockedTags(next);
        setSaving(true);
        try {
            await api(token).put("/user/tag-preferences", { blocked_tags: next });
        } catch (err) {
            setBlockedTags(previous);
            setMessage(errorMessage(err), "error");
            console.error(err);
        } finally {
            setSaving(false);
        }
    };

    const groups = Array.from(new Set(availableTags.map((tag) => tag.group)));

    return (
        <Screen paddingTopTitle={true} title={t("settings.contentPreferences.title")}>
            <Helmet>
                <title>Paçoca - {t("settings.contentPreferences.title")}</title>
            </Helmet>

            <p style={{ opacity: 0.7, fontSize: 14, padding: "0 10px" }}>
                {t("settings.contentPreferences.description")}
            </p>

            {loading ? (
                <div style={{ display: "flex", justifyContent: "center", marginTop: 20 }}>
                    <Spinner />
                </div>
            ) : (
                groups.map((group) => (
                    <div key={group} style={{ padding: "0 10px", marginBottom: 20 }}>
                        <h6 style={{ opacity: 0.8, marginBottom: 10 }}>
                            {t(`settings.contentPreferences.groups.${group}`)}
                        </h6>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                            {availableTags.filter((tag) => tag.group === group).map((tag) => {
                                const active = blockedTags.includes(tag.key);
                                return (
                                    <button
                                        key={tag.key}
                                        type="button"
                                        disabled={saving}
                                        aria-pressed={active}
                                        onClick={() => toggleTag(tag.key)}
                                        className={`btn btn-sm ${active ? "btn-danger" : "btn-outline-secondary"}`}
                                        style={{ borderRadius: 20, display: "flex", alignItems: "center", gap: 4 }}
                                    >
                                        {active ? <Check size={14} /> : null}
                                        {t(`settings.contentPreferences.tags.${tag.key}`)}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                ))
            )}
        </Screen>
    );
};

export default ContentPreferences;
